/**
 * Transfer Process service targeting the v5beta multi-tenant API.
 *
 * Endpoint pattern: /v5beta/participants/{participantContextId}/transferprocesses[/...]
 *
 * The participant context ID is read from the `participant_context_id` claim
 * of the current access token via TokenClaimsService.
 *
 * Auth headers are added by AuthHttpInterceptor.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TransferProcessState, TransferProcess, TransferProcessInput, QuerySpec, IdResponse } from '../model';
import { ManagementRouteService } from '../common/managementRoute.service';
import { MANAGEMENT_API_CONTEXT } from '../variables';

@Injectable({
  providedIn: 'root',
})
export class TransferProcessService {
  constructor(
    private readonly http: HttpClient,
    private readonly managementRoute: ManagementRouteService
  ) {}

  // ── Overloads kept for backward-compat with callers that pass observe param ──

  public cancelTransferProcess(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public cancelTransferProcess(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public cancelTransferProcess(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public cancelTransferProcess(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling cancelTransferProcess.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post(`${url}/transferprocesses/${encodeURIComponent(id)}/terminate`, {
      '@context': ['https://w3id.org/edc/connector/management/v2'],
      '@type': 'TerminateTransfer',
      reason: 'Call by DataDashboard.',
    });
  }

  public deprovisionTransferProcess(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<any>;
  public deprovisionTransferProcess(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<any>>;
  public deprovisionTransferProcess(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<any>>;
  public deprovisionTransferProcess(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling deprovisionTransferProcess.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post(`${url}/transferprocesses/${encodeURIComponent(id)}/deprovision`, null);
  }

  public getTransferProcess(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<TransferProcess>;
  public getTransferProcess(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<TransferProcess>>;
  public getTransferProcess(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<TransferProcess>>;
  public getTransferProcess(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling getTransferProcess.');
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<any>(`${url}/transferprocesses/${encodeURIComponent(id)}`).pipe(
      map(item => this.toTransferProcessInstance(item))
    );
  }

  public getTransferProcessState(id: string, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<TransferProcessState>;
  public getTransferProcessState(id: string, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<TransferProcessState>>;
  public getTransferProcessState(id: string, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<TransferProcessState>>;
  public getTransferProcessState(id: string): Observable<any> {
    if (!id) throw new Error('Required parameter id was null or undefined when calling getTransferProcessState.');

    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.get<TransferProcessState>(`${url}/transferprocesses/${encodeURIComponent(id)}/state`);
  }

  public initiateTransfer(transferRequestInput: TransferProcessInput, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<IdResponse>;
  public initiateTransfer(transferRequestInput: TransferProcessInput, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<IdResponse>>;
  public initiateTransfer(transferRequestInput: TransferProcessInput, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<IdResponse>>;
  public initiateTransfer(transferRequestInput: TransferProcessInput): Observable<any> {
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<any>(`${url}/transferprocesses`, this.toJsonLdTransferRequest(transferRequestInput)).pipe(
      map(response => ({
        ...response,
        id: response['@id'] ?? response['id'],
      }))
    );
  }

  public queryAllTransferProcesses(querySpec?: QuerySpec, observe?: 'body', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<Array<TransferProcess>>;
  public queryAllTransferProcesses(querySpec?: QuerySpec, observe?: 'response', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpResponse<Array<TransferProcess>>>;
  public queryAllTransferProcesses(querySpec?: QuerySpec, observe?: 'events', reportProgress?: boolean, options?: { httpHeaderAccept?: string; context?: HttpContext }): Observable<HttpEvent<Array<TransferProcess>>>;
  public queryAllTransferProcesses(querySpec?: QuerySpec): Observable<any> {
    
    const url = this.managementRoute.getManagementBaseUrl();
    return this.http.post<any[]>(`${url}/transferprocesses/request`, this.toJsonLdQuerySpec(querySpec)).pipe(
      map(items => items.map(item => this.toTransferProcessInstance(item)))
    );
  }

  private toTransferProcessInstance(raw: any): any {
    const id = raw['@id'] ?? raw['id'];
    const withAccessor = (obj: Record<string, any>) => ({
      ...obj,
      mandatoryValue: (prefix: string, name: string) =>
        obj?.[`${prefix}:${name}`] ?? obj?.[name] ?? null,
    });
    return {
      ...raw,
      id,
      '@id': id,
      state: raw['state'] ?? null,
      contractId: raw['contractId'] ?? null,
      assetId: raw['assetId'] ?? null,
      connectorId: raw['connectorId'] ?? raw['counterPartyId'] ?? null,
      mandatoryValue: (prefix: string, name: string) =>
        raw?.[`${prefix}:${name}`] ?? raw?.[name] ?? null,
      dataDestination: raw['dataDestination']
        ? withAccessor(raw['dataDestination'])
        : undefined,
    };
  }

  private toJsonLdTransferRequest(input: TransferProcessInput | Record<string, any>): Record<string, any> {
    if ((input as any)['@context']) {
      return input as Record<string, any>;
    }
    return {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'TransferRequest',
      ...(input as Record<string, any>),
    };
  }

  private toJsonLdQuerySpec(querySpec?: QuerySpec): Record<string, any> {
    const body: Record<string, any> = {
      '@context': [MANAGEMENT_API_CONTEXT],
      '@type': 'QuerySpec',
      offset: querySpec?.offset ?? 0,
      limit: querySpec?.limit ?? 50,
    };
    if (querySpec?.sortOrder) {
      body['sortOrder'] = querySpec.sortOrder;
    }
    if (querySpec?.sortField) {
      body['sortField'] = querySpec.sortField;
    }
    if (querySpec?.filterExpression?.length) {
      body['filterExpression'] = querySpec.filterExpression;
    }
    return body;
  }
}
